/**
 * The paragraph redline engine.
 *
 * Input: one paragraph as OOXML, the text the paragraph should read after the
 * edit, and the `Revision` to stamp. Output: the same paragraph with Word
 * tracked changes that turn its accepted text into the requested text.
 *
 * `RedlineEngine.redline` is the one entry point. Its body declares the six
 * steps as sub-functions and then runs them in order at the bottom:
 *
 *   1. parse the paragraph, or hand it back untouched when it is not XML;
 *   2. reconcile the clause marker so numbering is never struck by accident;
 *   3. restore the author's own earlier deletions the request asks back;
 *   4. plan the edits: a word diff tightened to characters
 *      (`TextEditPlanner.editsBetween`, the plan every caller shares);
 *   5. unlink the fields and equations the edits reach, so the text they cut
 *      is the document's own and Word has nothing left to redraw over it;
 *   6. apply the edits back to front.
 *
 * The same code runs in the add-in (Word's own DOM) and in the web app under
 * xmldom, where the core reaches it through the section write route; the
 * engine touches no host global beyond the parser and serializer `XmlHost`
 * hands out, which fails loudly on a server that installed none.
 */
import { wordDiff } from "./text/word-diff";
import { NO_HYPERLINK_TARGETS, type HyperlinkTargetsByRelationshipId } from "./ooxml/hyperlink-targets";
import { type EditWritingWordsOrLink, LinkFieldStriker, type LinksStruckWhole } from "./ooxml/link-field-striker";
import { WORDPROCESSINGML_NS } from "./ooxml/ooxml-names";
import { ParagraphModel, type PendingChange, type PendingChangeKind, type TextPiece } from "./ooxml/paragraph-model";
import { ParagraphRevisionMarks } from "./ooxml/paragraph-revision-marks";
import { revertTargetNamesOn } from "./ooxml/revert-target-mark-attribute";
import { type OwnInsertionEditPolicy, ParagraphSplicer, type PlannedEdit, type RestorableRange } from "./paragraph-splicer";
import { Revision } from "./ooxml/revision";
import { RunSpelling } from "./ooxml/run-spelling";
import { WordOwnedTextUnlinker } from "./ooxml/word-owned-text-unlinker";
import { TextEditPlanner } from "./text-edit-planner";
import { WordTokenizer } from "./text/word-tokenizer";
import { XmlHost } from "./xml-host";
import { type LinkInstruction, NO_LINK_INSTRUCTIONS } from "./wire/link-instruction";

/** Author stamped on every tracked change when the caller has no user to attribute. */
export const DIOPTRA_AUTHOR = "Dioptra AI";

/** Where revision ids start inside one redlined paragraph; high enough never to collide with ids Word wrote itself. */
export const FIRST_REVISION_ID = 9000;

/** The paragraph children that carry no content and so never separate a deletion from the copy written right after it. */
const CONTENTLESS_MARKERS = new Set(["bookmarkStart", "bookmarkEnd", "commentRangeStart", "commentRangeEnd", "proofErr", "permStart", "permEnd"]);

/**
 * A character range in a paragraph's accepted text, the coordinate system the
 * engine plans in. A span in any other coordinate system (a run, the redline's
 * own text) must be converted before it is handed to the engine.
 */
export interface AcceptedTextSpan {
    startOffset: number;
    endOffset: number;
}

export interface ParagraphRedlineRequest {
    paragraphXml: string;
    /** What the paragraph, or the `editableSpan` of it, should read after the edit. */
    textToWrite: string;
    /** Limits the edit to a span of the accepted text; null redlines the whole paragraph. */
    editableSpan: AcceptedTextSpan | null;
    /**
     * The addresses of the package's `w:hyperlink` elements (`hyperlinkTargetsOf`), so a
     * link the edits empty is struck whole as a HYPERLINK field.
     *
     * NULL
     *   Absent when the caller has only the paragraph, not its package (`TextChanges`):
     *   an element-form link the edits empty then has its runs struck inside it.
     */
    hyperlinkTargets?: HyperlinkTargetsByRelationshipId;
    /**
     * What the card said becomes of each link whose words it changes in this paragraph
     * (`RedlineTextParagraph.linkInstructions`): kept around the new words, struck and
     * written anew to another address, or struck with the new words written as plain text
     * (`LinkFieldStriker.strikeLinksTheEditsReplace`).
     *
     * NULL
     *   Absent when the caller writes words no card instructed (a revert's restored words,
     *   a user's own edit laid over a card): every link the edits reach is judged by its words.
     */
    linkInstructions?: readonly LinkInstruction[];
    /**
     * How an edit that deletes this author's own pending insertion is written: struck inside it for a
     * card's preview, removed outright for the document (`OwnChangeWriting`).
     */
    ownChangeWriting: OwnChangeWriting;
}

export interface RedlineOutcome {
    paragraphXml: string;
    /** Planned edits applied; zero means the paragraph already read as requested and was handed back as it came. */
    appliedChangeCount: number;
}

/**
 * The pending change the caller read, by which the engine checks that the id it
 * was handed still names the same change: kind and author as the reader's tag
 * says them, text as the change's words read (the struck words of a deletion,
 * the surviving words of an insertion).
 */
export interface ExpectedPendingChange {
    kind: PendingChangeKind;
    author: string;
    text: string;
}

/**
 * How an edit or a revert that reaches this author's own pending change is written.
 *
 * CASES
 *   - "asWord"          -> as Word writes it: an own insertion the edit deletes is removed outright, an
 *                          own deletion a revert takes back is un-struck in place. What Apply writes into
 *                          the document.
 *   - "asTrackedChange" -> as over another author's change: the deleted words of an own insertion struck
 *                          by this author inside it, a copy of the deleted words beside a reverted
 *                          deletion. What a card's preview draws, so a card that deletes or takes back
 *                          its author's own pending words shows the words it removes for review; never
 *                          written into the document.
 *
 * EXAMPLE
 *   paragraph  "Workaround within " + ins(Counsel) "two (2) hours"
 *   edit       "two (2) hours" -> "one (1) hour", by Counsel
 *   asWord          -> ins(Counsel) "Workaround within one (1) hour"   (Word's own result after Apply)
 *   asTrackedChange -> ins(Counsel) "Workaround within " + del(Counsel) "two (2) hours" + ins "one (1) hour"
 */
export type OwnChangeWriting = "asWord" | "asTrackedChange";

/**
 * What the splicer does with this author's own insertion that an edit deletes, for a writing.
 *
 * CASES
 *   - "asTrackedChange" -> "strike": the preview shows the words taken back
 *   - "asWord"          -> "vacate": the document loses them, as Word does
 */
function ownInsertionEditPolicyOf(ownChangeWriting: OwnChangeWriting): OwnInsertionEditPolicy {
    /** WHEN the card's preview is drawn CONSEQUENCE the words its author takes back are struck, so the user sees what Apply removes. */
    if (ownChangeWriting === "asTrackedChange") return "strike";
    return "vacate";
}

/** How a revert names its change: the reader's id, and the change as it was read. */
export interface RevertTargetName {
    /** The reader's id of the change to revert: "c1", "c2", …, as `ParagraphModel.pendingChanges` numbers them. */
    changeId: string;
    expectedChange: ExpectedPendingChange;
}

/**
 * The marks a run laid on the page as first read, before any of its reverts was written
 * (`RevertTargetMarks.layOn`): every revert of the run is in exactly one of the two.
 */
export interface RevertTargetsOfTheRun {
    /** The reverts whose change the first read found, by name: each change's revision elements carry the name. */
    markedRevertNames: ReadonlySet<string>;
    /** The reverts whose change the first read did not find, by name, each with the re-ask that read gave. */
    unresolvedRevertReasks: ReadonlyMap<string, RevertChangeReask>;
}

/** One revert's name in a run that laid marks, and that run's marks. */
export interface RevertMarkOfTheRun {
    revertName: string;
    targetsOfTheRun: RevertTargetsOfTheRun;
}

export interface RevertChangeRequest extends RevertTargetName {
    paragraphXml: string;
    /**
     * The run's mark for this revert's change: within a run that laid marks, the revert is
     * resolved by its mark alone (`changesNamed`).
     *
     * NULL
     *   Absent for a revert resolved alone, outside any run (the engine's own tests): it is
     *   resolved by the change as read, and its id.
     */
    markOfTheRun?: RevertMarkOfTheRun;
    /** For a deletion only: how the restored words should read instead of exactly as they were; null brings them back as they were. */
    text: string | null;
    ownChangeWriting: OwnChangeWriting;
    /**
     * The package's link addresses, for the redline `text` makes on the restored words
     * (`ParagraphRedlineRequest.hyperlinkTargets`), so a link that redline empties is
     * struck whole as its HYPERLINK field.
     *
     * NULL
     *   Absent when the caller has only the paragraph: an element-form link that redline
     *   empties then has its runs struck inside it.
     */
    hyperlinkTargets?: HyperlinkTargetsByRelationshipId;
    /**
     * The text the card writes into the paragraph once its reverts are done, so a revert of
     * an insertion knows whether the card writes words into the gap it leaves
     * (`cardWritesWordsIntoTheGap`).
     *
     * NULL
     *   Null or absent when the caller does not know it (the web's revert planning):
     *   decision 5 holds.
     */
    textTheCardWritesAfter?: string | null;
}

export interface RevertChangeOutcome {
    paragraphXml: string;
    /** One for the revert, plus the edits the given `text` made on the restored words. */
    appliedChangeCount: number;
}

/** One pending change as a re-ask lists it, so the caller can name the right one next time. */
export interface PendingChangeSummary {
    id: string;
    kind: PendingChangeKind;
    author: string;
    text: string;
}

/**
 * Why a revert was handed back.
 *   - unknownChange     -> no pending change of the paragraph reads as the one the caller read, and none carries the id
 *   - staleChange       -> a change carries the id, but its kind, author or words are not the ones the caller read, and no other change reads as them
 *   - ambiguousChange   -> several changes read as the one the caller read and none of them carries the id
 *   - textOnInsertion   -> text was given for an insertion, which is only struck or removed
 *   - nothingToRevert   -> the insertion holds no surviving word: every word of it is already struck
 *   - paragraphStruck   -> the paragraph's own mark is struck; a change inside a struck paragraph is not reverted yet
 */
export type RevertChangeReaskReason = "unknownChange" | "staleChange" | "ambiguousChange" | "textOnInsertion" | "nothingToRevert" | "paragraphStruck";

export class RevertChangeReask extends Error {
    constructor(
        readonly reason: RevertChangeReaskReason,
        readonly pendingChanges: PendingChangeSummary[],
        message: string,
    ) {
        super(message);
        this.name = "RevertChangeReask";
    }
}

export class RedlineEngine {
    constructor(private readonly revision: Revision) {}

    /**
     * Redlines one paragraph. Returns the paragraph unchanged with
     * `appliedChangeCount: 0` when the XML does not parse or the accepted text
     * already reads as requested (ignoring whitespace). An edit that reaches a
     * field result or an equation unlinks that construct first, so the tracked
     * change lands in the document's own text. Throws `XmlHostMissingError`
     * when the running host has no XML parser (server-side code that never
     * installed xmldom's).
     */
    redline(request: ParagraphRedlineRequest): RedlineOutcome {
        // Browsers answer malformed XML with a parsererror document; xmldom throws. Both mean "leave the paragraph alone".
        // The parser is taken outside the try: a host with no parser is a setup fault, never "nothing to change".
        const parseParagraph = (): Document | null => {
            const domParser = XmlHost.domParser();
            try {
                const parsed = domParser.parseFromString(request.paragraphXml, "application/xml");
            const parserErrorNodes = parsed.getElementsByTagName("parsererror");
            const parsedDocumentHasErrors = parserErrorNodes.length > 0;
                /**
                 * CASES
                 *   - parsererror nodes found -> return null
                 *   - none found              -> return the parsed XML document
                 *
                 * NULL
                 *   Browser-style parse failure. Caller leaves this paragraph untouched.
                 */
                return parsedDocumentHasErrors ? null : parsed;
            } catch {
                return null;
            }
        };

        const doc = parseParagraph();
        /**
         * WHEN parsing failed
         *   CONSEQUENCE
         *     Return the input paragraph unchanged with no applied changes.
         */
        if (!doc) return { paragraphXml: request.paragraphXml, appliedChangeCount: 0 };
        const revision = this.revision;
        const model = new ParagraphModel(doc.documentElement);
        /** A preview strikes the words of this author's own insertion that the edit deletes; the document write vacates them, as Word does. */
        const splicer = new ParagraphSplicer(model, revision, ownInsertionEditPolicyOf(request.ownChangeWriting));
        /**
         * CASES
         *   - request.editableSpan given -> copy that accepted-text span
         *   - request.editableSpan null  -> use 0..model.text.length
         */
        const span: AcceptedTextSpan = request.editableSpan ? { ...request.editableSpan } : { startOffset: 0, endOffset: model.text.length };

        /**
         * The request as the engine should diff it. Requests arrive from
         * HTML-ish sources, so a `<br>` means a hard line break, and from
         * Windows sources, so a line may end in "\r\n" or a lone "\r". Each of
         * them is written "\n", the one spelling the paragraph's text uses for
         * its `w:br`. Nothing else is changed: a request that leaves out the
         * paragraph's typed clause number strikes it, as the request reads.
         *
         * WHY every line ending is spelled "\n"
         *   Word has one line-break element and the paragraph model reads it
         *   as "\n". Compared character by character, a request's "\r\n" over
         *   the paragraph's "\n" planned the "\r" as an insertion, and the
         *   splicer wrote it as a second `w:br` in front of the paragraph's own.
         *
         * EXAMPLE
         *   textToWrite  "Line one\r\nLine three"   paragraph "Line one" <w:br/> "Line two"
         *   diffed as    "Line one\nLine three"   -> "two" struck, "three" inserted, one w:br
         */
        const requestTextWithLineBreaks = (): string => {
            const LINE_BREAK_TAG = /<br\s*\/?>/gi;
            /**
             * CASES, per line ending
             *   - "\r\n"      -> "\n": the "\r" and the "\n" after it are one line ending
             *   - a lone "\r" -> "\n"
             *   - "\n"        -> not matched: it is already the one spelling
             */
            const WINDOWS_OR_CARRIAGE_RETURN_LINE_ENDING = /\r\n?/g;
            return request.textToWrite.replace(LINE_BREAK_TAG, "\n").replace(WINDOWS_OR_CARRIAGE_RETURN_LINE_ENDING, "\n");
        };

        /**
         * The visible-first restore rule. The paragraph may hold tracked
         * deletions by this author from an earlier round whose words the request
         * contains again. Only where the diff of the visible text against the
         * request shows an insert or a replace do we look at those hidden
         * deletions for words to bring back; a hidden word that matches a word
         * the visible text already supplies stays deleted. Without this rule a
         * paragraph that once lost " the " and later gained a different "the"
         * revived the old one as well and doubled the word.
         *
         * Returns the number of characters restored; the span is widened by the
         * same amount because those characters are live text now.
         */
        const restoreOwnDeletions = (requestText: string): number => {
            interface SourceChar {
                isDeletedText: boolean;
                piece: TextPiece;
                offsetInPiece: number;
            }
            /** A word of the visible text with its start and end as indexes into the source `chars` list, not text offsets. */
            interface VisibleWord {
                text: string;
                firstCharIndex: number;
                endCharIndex: number;
            }
            interface RestorationGap {
                fromCharIndex: number;
                toCharIndex: number;
                wantedWords: string[];
            }

            // Every live character inside the span, interleaved with the characters
            // of deletions this author made. A deleted piece is a candidate only when
            // it was original text, sits inside the span, and is not wrapped by
            // another author's revision, whose review we must not disturb.
            const sourceSequence = (): { text: string; chars: SourceChar[] } => {
                let text = "";
                const chars: SourceChar[] = [];
                for (const piece of model.pieces) {
                    const content = piece.text;
                    /**
                     * CASES
                     *   - live piece     -> copy visible characters inside the span into the diff source first
                     *   - deleted piece  -> test the restore rules below
                     *
                     * WHY
                     *   Every visible character in the span belongs in the visible-vs-request diff,
                     *   even when hidden deletions sit nearby.
                     */
                    if (!piece.isDeleted) {
                        for (let offsetInPiece = 0; offsetInPiece < content.length; offsetInPiece++) {
                            const accepted = piece.startOffset + offsetInPiece;
                            const acceptedBeforeSpanStart = accepted < span.startOffset;
                            const acceptedAtOrAfterSpanEnd = accepted >= span.endOffset;
                            const acceptedFallsOutsideSpan = acceptedBeforeSpanStart || acceptedAtOrAfterSpanEnd;
                            /**
                             * WHEN a live character falls outside the editable span
                             *   CONSEQUENCE
                             *     Skip it.
                             *
                             *
                             * EXAMPLE
                             *   span = 10..15
                             *   kept offsets    -> 10, 11, 12, 13, 14
                             *   skipped offsets -> 9, 15
                             */
                            if (acceptedFallsOutsideSpan) continue;
                            text += content[offsetInPiece];
                            chars.push({ isDeletedText: false, piece, offsetInPiece });
                        }
                        /**
                         * WHEN all live characters from this non-deleted piece are copied
                         *   CONSEQUENCE
                         *     Move to the next piece.
                         *     The deleted-piece restore rules do not run for visible text.
                         */
                        continue;
                    }
                    /**
                     * EDGE CASE
                     *   piece.startOffset = span.endOffset -> still restorable
                     *
                     * EXAMPLE
                     *   span              = 10..15
                     *   piece.startOffset = 15
                     *   deleted text      = " world"
                     *   requestText       = "Hello world"
                     *
                     * WHY
                     *   A reinsertion at the right edge may need this boundary deletion back
                     *   instead of planning a fresh insert.
                     */
                    const restorable = revision.isBy(piece.deletedBy)
                        && piece.insertedBy === null
                        && piece.startOffset >= span.startOffset
                        && piece.startOffset <= span.endOffset
                        && !splicer.wrappedByAnotherAuthor(piece);
                    /**
                     * WHEN a deleted piece is not restorable
                     *   CONSEQUENCE
                     *     Skip it.
                     *
                     *
                     * CASES
                     *   - deleted by another author                -> skip
                     *   - outside the editable span                -> skip
                     *   - inserted, then deleted                   -> skip
                     *   - wrapped by another author's revision     -> skip
                     */
                    if (!restorable) continue;
                    for (let offsetInPiece = 0; offsetInPiece < content.length; offsetInPiece++) {
                        text += content[offsetInPiece];
                        chars.push({ isDeletedText: true, piece, offsetInPiece });
                    }
                }
                return { text, chars };
            };
            const visibleWords = (text: string, chars: SourceChar[]): VisibleWord[] => {
                const charIndexes: number[] = [];
                let shown = "";
                chars.forEach((char, index) => {
                    /**
                     * WHEN the character is hidden deleted text
                     *   CONSEQUENCE
                     *     Leave it out of visible-word tokenization.
                     *
                     *
                     * WHY
                     *   Only currently shown text defines the visible side of the diff.
                     */
                    if (char.isDeletedText) return;
                    charIndexes.push(index);
                    shown += text[index];
                });
                return WordTokenizer.positioned(shown).map((token) => ({
                    text: token.text,
                    firstCharIndex: charIndexes[token.startOffset],
                    /**
                     * EXAMPLE
                     *   token.endOffset        = 5
                     *   last visible offset    = 4
                     *   charIndexes[4]         = 9
                     *   endCharIndex           = 10
                     *
                     * WHY
                     *   Convert the token's last visible character back to an exclusive index in
                     *   `chars`.
                     */
                    endCharIndex: charIndexes[token.endOffset - 1] + 1,
                }));
            };
            // Each revived word takes its adjoining hidden whitespace with it, so the
            // restored text rejoins its neighbours with the original spacing. Returns
            // the indexes into `chars` that come back to life.
            const restoredWithinGap = (text: string, chars: SourceChar[], gap: RestorationGap): number[] => {
                const hiddenCharIndexes: number[] = [];
                let hiddenText = "";
                for (let index = gap.fromCharIndex; index < gap.toCharIndex; index++) {
                    /**
                     * WHEN the gap character is live text
                     *   CONSEQUENCE
                     *     Skip it.
                     *
                     *
                     * WHY
                     *   Live characters are already present. Only hidden deleted characters can be
                     *   restored.
                     */
                    if (!chars[index].isDeletedText) continue;
                    hiddenCharIndexes.push(index);
                    hiddenText += text[index];
                }
                const hiddenWords = WordTokenizer.positioned(hiddenText);
                /**
                 * WHEN the hidden gap contains no deleted words
                 *   CONSEQUENCE
                 *     Return no restorations.
                 *
                 *
                 * RETURNS
                 *   An empty list of char indexes to restore.
                 */
                if (hiddenWords.length === 0) return [];
                const restored: number[] = [];
                for (const op of wordDiff(
                    hiddenWords.map((token) => WordTokenizer.comparisonKey(token.text)),
                    gap.wantedWords.map(WordTokenizer.comparisonKey),
                )) {
                    /**
                     * WHEN this diff hunk is not `equal`
                     *   CONSEQUENCE
                     *     Skip it.
                     *
                     *
                     * WHY
                     *   Only deleted words that exactly match wanted words in this gap come back.
                     *   Insert-only and delete-only hunks stay hidden.
                     */
                    if (op.tag !== "equal") continue;
                    for (let wordIndex = op.beforeStart; wordIndex < op.beforeEnd; wordIndex++) {
                        /**
                         * CASES
                         *   - wordIndex > 0 -> start after the previous hidden word
                         *   - wordIndex = 0 -> start at hiddenText offset 0
                         *
                         * EXAMPLE
                         *   hiddenWords = [{endOffset: 3}, {endOffset: 8}, ...]
                         *   wordIndex   = 1
                         *   wordIndex-1 = 0
                         *   leading     = 3
                         */
                        const leading = wordIndex > 0 ? hiddenWords[wordIndex - 1].endOffset : 0;
                        const nextHiddenWordIndex = wordIndex + 1;
                        const nextHiddenWordExists = nextHiddenWordIndex < hiddenWords.length;
                        /**
                         * CASES
                         *   - next hidden word exists -> stop before that word
                         *   - this is the last word   -> stop at hiddenText.length
                         *
                         * EXAMPLE
                         *   wordIndex               = 1
                         *   hiddenWords.length      = 4
                         *   wordIndex+1             = 2
                         *   hiddenWords[2].startOffset = 12
                         *   trailing                = 12
                         */
                        const trailing = nextHiddenWordExists ? hiddenWords[wordIndex + 1].startOffset : hiddenText.length;
                        /**
                         * EXAMPLE
                         *   leading           = 3
                         *   trailing          = 7
                         *   offsets taken     = 3, 4, 5, 6
                         *   hiddenCharIndexes = [5, 6, 9, 10, 11, 14, 15]
                         *   restored adds     = 10, 11, 14, 15
                         *
                         * WHY
                         *   Convert hidden-text offsets in [leading, trailing) back to `chars` indexes.
                         */
                        for (let offset = leading; offset < trailing; offset++) restored.push(hiddenCharIndexes[offset]);
                    }
                }
                return restored;
            };
            const restorableRanges = (): RestorableRange[] => {
                const { text, chars } = sourceSequence();
                const charsContainDeletedText = chars.some((char) => char.isDeletedText);
                /**
                 * WHEN the span contains no hidden deleted characters
                 *   CONSEQUENCE
                 *     Return no restorable ranges.
                 *
                 *
                 * RETURNS
                 *   An empty list of deletion ranges to revive before planning fresh edits.
                 */
                if (!charsContainDeletedText) return [];
                const visible = visibleWords(text, chars);
                const wanted = WordTokenizer.words(requestText);
                const restored = new Set<number>();
                for (const op of wordDiff(
                    visible.map((word) => WordTokenizer.comparisonKey(word.text)),
                    wanted.map(WordTokenizer.comparisonKey),
                )) {
                    const opIsEqualHunk = op.tag === "equal";
                    const opIsDeleteHunk = op.tag === "delete";
                    const opNeedsNoRestorationCheck = opIsEqualHunk || opIsDeleteHunk;
                    /**
                     * WHEN the visible-vs-request diff hunk is `equal` or `delete`
                     *   CONSEQUENCE
                     *     Skip it.
                     *
                     *
                     * WHY
                     *   Only insert and replace hunks can justify reviving hidden words.
                     */
                    if (opNeedsNoRestorationCheck) continue;
                    const gap: RestorationGap = {
                        /**
                         * CASES
                         *   - op.beforeStart > 0 -> start after the previous visible word
                         *   - op.beforeStart = 0 -> start at char index 0
                         */
                        fromCharIndex: op.beforeStart > 0 ? visible[op.beforeStart - 1].endCharIndex : 0,
                        /**
                         * CASES
                         *   - op.beforeEnd < visible.length -> stop before the next visible word
                         *   - op.beforeEnd = visible.length -> stop at chars.length
                         */
                        toCharIndex: op.beforeEnd < visible.length ? visible[op.beforeEnd].firstCharIndex : chars.length,
                        /**
                         * EXAMPLE
                         *   op.afterStart = 2
                         *   op.afterEnd   = 5
                         *   slice(2, 5)   -> entries 2, 3, 4
                         *
                         * WHY
                         *   Take exactly the request words for this insert-or-replace hunk.
                         */
                        wantedWords: wanted.slice(op.afterStart, op.afterEnd),
                    };
                    for (const index of restoredWithinGap(text, chars, gap)) restored.add(index);
                }
                const ranges: RestorableRange[] = [];
                let current: RestorableRange | null = null;
                for (let index = 0; index < chars.length; index++) {
                    /**
                     * WHEN this character was not selected for restoration
                     *   CONSEQUENCE
                     *     End any current run here.
                     *
                     *
                     * WHY
                     *   Separate revived stretches must become separate splice ranges.
                     */
                    if (!restored.has(index)) {
                        current = null;
                        /**
                         * WHEN the current character is not in the restored set
                         *   CONSEQUENCE
                         *     Move on.
                         *     This character contributes to no restorable range.
                         */
                        continue;
                    }
                    const char = chars[index];
                    /**
                     * WHEN the selected character is the next character in the same piece
                     *   CONSEQUENCE
                     *     Extend the current range.
                     *
                     *
                     * EDGE CASE
                     *   adjacent offsets 4 then 5 in one piece -> one range
                     *   piece change or offset gap             -> start a new range
                     */
                    if (current && current.piece === char.piece && current.toOffsetInPiece === char.offsetInPiece) {
                        /**
                         * EXAMPLE
                         *   current range   = 4..5
                         *   char.offsetInPiece = 5
                         *   new end         = 6
                         *   merged range    = 4..6
                         *
                         * WHY
                         *   `toOffsetInPiece` is exclusive, so the next adjacent character extends
                         *   the same half-open range.
                         */
                        current.toOffsetInPiece = char.offsetInPiece + 1;
                        /**
                         * WHEN the current range was extended
                         *   CONSEQUENCE
                         *     Keep growing that range instead of opening another one.
                         */
                        continue;
                    }
                    /**
                     * EXAMPLE
                     *   char.offsetInPiece = 7
                     *   new range          = 7..8
                     *
                     * WHY
                     *   Start a one-character half-open range in this piece.
                     */
                    current = { piece: char.piece, fromOffsetInPiece: char.offsetInPiece, toOffsetInPiece: char.offsetInPiece + 1 };
                    ranges.push(current);
                }
                return ranges;
            };

            const ranges = restorableRanges();
            /**
             * WHEN no deleted runs matched the request
             *   CONSEQUENCE
             *     Return 0.
             *
             *
             * RETURNS
             *   The number of restored characters.
             */
            if (ranges.length === 0) return 0;
            splicer.restoreDeletions(ranges);
            model.refresh();
            /**
             * EXAMPLE
             *   range 1 = 2..5 -> length 3
             *   range 2 = 9..10 -> length 1
             *   total   = 4
             *
             * RETURNS
             *   The number of restored characters across all piece-local ranges.
             */
            const restoredCharacterCount = ranges.reduce((sum, range) => sum + (range.toOffsetInPiece - range.fromOffsetInPiece), 0);
            span.endOffset += restoredCharacterCount;
            return restoredCharacterCount;
        };

        /**
         * Word-diffs the span text against the request and tightens each hunk to
         * characters, returning edits in paragraph (accepted-text) coordinates.
         * Diffing words keeps a change readable; tightening to characters keeps
         * the shared whitespace out of the change so spacing is never struck and
         * reinserted.
         *
         * WHY the plan is `TextEditPlanner.editsBetween`
         *   It is the one plan every caller that lays a change where the engine
         *   lays it uses: the collision reader and the add-in's cell writer read
         *   the same edits. The engine adds what only the paragraph knows, which
         *   stretches are an equation's characters, and moves the edits from the
         *   span's offsets to the paragraph's.
         */
        const planChanges = (sourceText: string, requestText: string): PlannedEdit[] => {
            /**
             * Whether a stretch of the editable span is an equation's characters.
             *
             * WHY it changes the edit
             *   Prose is edited a word at a time; a formula has no words. `P(1 + r)n,`
             *   is one token to the tokenizer, so a word-sized cut would strike the
             *   whole formula and write it back as ordinary text. Inside an equation
             *   the unit is the character, so the shared edges are trimmed all the way
             *   and only `r` is struck.
             *
             * EXAMPLE
             *   localStartOffset = 24, localEndOffset = 28, span.startOffset = 0
             *   pieces 24-32 are the runs of `<m:oMath>` -> true
             */
            const cutIsInsideEquation = (localStartOffset: number, localEndOffset: number): boolean => {
                const paragraphStartOffset = localStartOffset + span.startOffset;
                const paragraphEndOffset = localEndOffset + span.startOffset;
                return model
                    .acceptedPieces()
                    .some((piece) => piece.startOffset < paragraphEndOffset && piece.endOffset > paragraphStartOffset && RunSpelling.isMath(piece.run));
            };
            return TextEditPlanner.editsBetween(sourceText, requestText, { isEquationStretch: cutIsInsideEquation }).map((edit) => ({
                /**
                 * EXAMPLE
                 *   span.startOffset = 30
                 *   edit.startOffset = 4
                 *   paragraph start  = 34
                 */
                startOffset: edit.startOffset + span.startOffset,
                /**
                 * EXAMPLE
                 *   span.startOffset = 30
                 *   edit.endOffset   = 9
                 *   paragraph end    = 39
                 *
                 * WHY
                 *   Translate the local exclusive end back to paragraph coordinates.
                 */
                endOffset: edit.endOffset + span.startOffset,
                replacementText: edit.replacementText,
            }));
        };

        /**
         * Turns every field result and equation the planned edits reach into
         * ordinary runs before a single character is cut, so the tracked change
         * the splicer is about to write is written into the document's own text.
         *
         * WHY
         *   Word redraws a live field's result on the next field update and would
         *   throw the tracked change away with it; an equation's characters live in
         *   math runs that carry no tracked-change spelling. Unlinking leaves the
         *   same characters at the same offsets, so the edits planned above still
         *   name exactly the text they were planned against.
         */
        const unlinkWordOwnedTextUnderEdits = (edits: PlannedEdit[]): void => {
            WordOwnedTextUnlinker.unlinkConstructsReachedBy(model, edits);
        };

        /**
         * The planned edits moved past the text `LinkFieldStriker` struck, which no
         * longer reads: each edit keeps what it writes and loses the struck characters
         * it covered, struck characters no edit deleted ("the " of "the Annex B") are
         * written back as an insertion where they stood, edits that meet once moved
         * become one, and an edit left with nothing to delete or write is dropped.
         *
         * WHY move the plan rather than plan again
         *   The plan already says what the request deletes around the link, the space
         *   beside it included. Planned again on "see  now", the request "see now"
         *   reads the same once collapsed and the second space would stay.
         *
         * EXAMPLE, link "the Fee Schedule" struck at 4..20 of "see the Fee Schedule now"
         *   edit 3..20 -> ""         `3 - 0`, `20 - 16` -> 3..4 -> "": the space before the link
         *   edit 4..20 -> "Annex B"  `4 - 0`, `20 - 16` -> 4..4 -> "Annex B": an insertion beside the struck link
         *   edit 21..24 -> "today"   `21 - 16`, `24 - 16` -> 5..8 -> "today"
         *   request "see the Annex B now": edit 8..20 -> "Annex B", "the " at 4..8 deleted by no edit
         *     -> "the " written back at 4..4, met by "Annex B" at 4..4 -> one insertion "the Annex B"
         *
         * WHEN the link was struck for a "change"
         *   CONSEQUENCE
         *     every edit that reaches its text, and the struck text written back, carries the new
         *     link (`LinkFieldStriker.replacementLinkReachedBy`), read before the edits move; they
         *     merge into one insertion the splicer writes as that link: "the Annex B" above, as
         *     a link to the new address.
         */
        const editsMovedPastStruckLinks = (plannedEdits: PlannedEdit[], linksStruckWhole: LinksStruckWhole): EditWritingWordsOrLink[] => {
            /**
             * EXAMPLE, struck 4..20
             *   offset 3  -> 0 struck characters before it
             *   offset 20 -> `min(20, 20) - 4` = 16
             *   offset 24 -> `min(24, 20) - 4` = 16
             */
            const struckCharactersBefore = (offset: number): number =>
                linksStruckWhole.struckRanges.reduce((characterCount, struckRange) => characterCount + Math.max(0, Math.min(offset, struckRange.endOffset) - struckRange.startOffset), 0);
            const plannedEditsWritingWordsOrLink = plannedEdits.map((plannedEdit) => ({ ...plannedEdit, replacementLink: LinkFieldStriker.replacementLinkReachedBy(plannedEdit, linksStruckWhole) }));
            const editsWithStruckTextTheRequestKeeps = [...plannedEditsWritingWordsOrLink, ...LinkFieldStriker.struckTextTheEditsKeep(linksStruckWhole.struckRanges, plannedEdits)]
                .sort((left, right) => left.startOffset - right.startOffset || left.endOffset - right.endOffset);
            const movedEdits = editsWithStruckTextTheRequestKeeps.map((plannedEdit) => ({
                ...plannedEdit,
                startOffset: plannedEdit.startOffset - struckCharactersBefore(plannedEdit.startOffset),
                endOffset: plannedEdit.endOffset - struckCharactersBefore(plannedEdit.endOffset),
            }));
            return LinkFieldStriker.mergedTouchingEdits(movedEdits).filter((movedEdit) => movedEdit.endOffset > movedEdit.startOffset || movedEdit.replacementText.length > 0);
        };

        /** The planned edits as the engine writes them when no link was struck: every edit writes plain words. */
        const editsWritingPlainWords = (plannedEdits: PlannedEdit[]): EditWritingWordsOrLink[] =>
            plannedEdits.map((plannedEdit) => ({ ...plannedEdit, replacementLink: null }));

        // Back to front, so each splice leaves every earlier edit's offsets untouched.
        const apply = (edits: EditWritingWordsOrLink[]): void => {
            /** Read once every link the plan strikes is struck: only a link with live text can be written in front of. */
            const linkStarts = LinkFieldStriker.linkStartsOf(model);
            for (const edit of [...edits].reverse()) {
                /**
                 * CASES
                 *   - a pure insertion at a link's start -> written in front of the element that opens
                 *                                           the link, outside it, as at the link's end
                 *                                           (`ParagraphSplicer.insertBefore`)
                 *   - any other edit                     -> spliced where it stands
                 */
                const linkStartingAtInsertion = edit.startOffset === edit.endOffset ? linkStarts.find((linkStart) => linkStart.startOffset === edit.startOffset) : undefined;
                if (linkStartingAtInsertion !== undefined) splicer.insertBefore(edit, linkStartingAtInsertion.linkOpeningElement, edit.replacementLink);
                else splicer.applyEdit(edit, edit.replacementLink);
            }
        };

        const requestText = requestTextWithLineBreaks();
        const restoredCharacterCount = restoreOwnDeletions(requestText);
        /**
         * EXAMPLE
         *   span.startOffset = 30
         *   span.endOffset   = 45
         *   slice(30, 45)    -> characters 30 through 44
         *
         * WHY
         *   Read the current editable accepted-text span after any restorations widened
         *   it.
         */
        const sourceText = model.text.slice(span.startOffset, span.endOffset);
        const collapsedTextsMatch = TextEditPlanner.requestWritesNothing(sourceText, requestText);
        /**
         * WHEN collapsed source text equals collapsed request text
         *   CONSEQUENCE
         *     Stop here with no new tracked changes.
         *
         *
         * WHY
         *   Whitespace-normalized equality means the span already reads as requested,
         *   even if raw spacing or line breaks differ. The test is the planner's
         *   (`TextEditPlanner.requestWritesNothing`), the one every caller reads.
         */
        if (collapsedTextsMatch) {
            return {
                /**
                 * CASES
                 *   - restoredCharacterCount > 0 -> return serialized updated XML
                 *   - restoredCharacterCount = 0 -> return the original input XML
                 */
                paragraphXml: restoredCharacterCount > 0 ? XmlHost.xmlSerializer().serializeToString(doc) : request.paragraphXml,
                appliedChangeCount: 0,
            };
        }
        const firstPlannedEdits = planChanges(sourceText, requestText);
        /**
         * WHY the unlink pass reads the edits as planned, before any link is struck
         *   A link inside a field Word redraws (a REF around it, a contents entry) is
         *   reached by the planned edits; moved past a struck link they no longer
         *   reach the outer field, which stayed live with an empty result that Word
         *   refills on the next field update. Unlinking keeps the accepted text
         *   character for character, so the striker reads the same offsets after it.
         */
        unlinkWordOwnedTextUnderEdits(firstPlannedEdits);
        /**
         * WHY the edits are kept within links before the striker reads them
         *   The planner edits a word with the punctuation that ends it, so an edit of
         *   a link's last word ran past the link's end ("Schedule." -> "Schedules.")
         *   and the new word was written after the field. Taking the unchanged "." off
         *   the edit leaves the same text and keeps the edit inside the link.
         */
        const editsWithinLinks = LinkFieldStriker.editsKeptWithinLinks(model, firstPlannedEdits);
        const linksStruckWhole = LinkFieldStriker.strikeLinksTheEditsReplace(model, editsWithinLinks, this.revision, request.hyperlinkTargets ?? NO_HYPERLINK_TARGETS, request.linkInstructions ?? NO_LINK_INSTRUCTIONS);
        /**
         * CASES
         *   - no link was struck      -> the plan stands, every edit writing plain words
         *   - a link was struck whole -> the plan, moved past the struck text, the words of a
         *                                "change" written as its new link
         */
        const edits = linksStruckWhole.linkCount === 0 ? editsWritingPlainWords(editsWithinLinks) : editsMovedPastStruckLinks(editsWithinLinks, linksStruckWhole);
        apply(edits);
        return { paragraphXml: XmlHost.xmlSerializer().serializeToString(doc), appliedChangeCount: edits.length + linksStruckWhole.linkCount };
    }

    /**
     * The paragraph's pending tracked changes as a revert names them: the id the
     * reader lists each under ("c1", "c2", …), its kind, its author and its words.
     * `RevertOperations` reads the change a `revert_change` operation names off
     * this list, taken from the paragraph as the model was shown it, before any
     * revert of the same answer moves the numbering.
     *
     * RETURNS
     *   [] for a paragraph with no pending change.
     */
    pendingChangesOf(paragraphXml: string): PendingChangeSummary[] {
        const doc = XmlHost.domParser().parseFromString(paragraphXml, "application/xml");
        return new ParagraphModel(doc.documentElement).pendingChanges().map(RedlineEngine.summaryOf);
    }

    /**
     * The change a revert names on a paragraph as it stands, read in place so a run can mark
     * the change's own elements (`RevertTargetMarks.layOn`): the same resolution `revertChange`
     * makes outside a run, by the change as read and its id.
     *
     * RETURNS
     *   One change, or the pieces typing inside it cut it into, in document order; each piece's
     *   `insertionContainer` and `deletionContainer` are the paragraph's own elements.
     *
     * THROWS
     *   `RevertChangeReask`, as `revertChange` would, when the paragraph holds no such change,
     *   or two read alike and neither carries the id.
     */
    static changesARevertNamesOn(paragraph: Element, revertTarget: RevertTargetName): PendingChange[] {
        const changes = new ParagraphModel(paragraph).pendingChanges();
        return RedlineEngine.changesNamed(revertTarget, changes, changes.map(RedlineEngine.summaryOf), undefined);
    }

    /**
     * Reverts one pending tracked change of the paragraph, named by the id the
     * reader gave it, and asks again when the id does not name the change the
     * caller read (the paragraph changed since).
     *
     * CASES, by the change's kind and author
     *   - another author's deletion -> their `w:del` stays; this author's `w:ins` holding a copy of
     *                                  their struck runs goes right after it (`insertCopyOfStruckRuns`)
     *   - this author's deletion    -> un-struck in place (`restoreDeletions`) when written "asWord";
     *                                  a copy beside it, as another author's, when written "asTrackedChange"
     *   - another author's insertion -> struck: this author's deletion over its surviving words
     *   - this author's insertion   -> removed outright, as Word removes an author's own insertion, when
     *                                  written "asWord"; struck like another author's when written
     *                                  "asTrackedChange" (`OwnChangeWriting`)
     *   - `text` on a deletion       -> once the words are back, `redline` edits them toward `text`
     *                                  inside the restored span alone, so each changed word takes the
     *                                  formatting of the word it replaces
     *   - another author's deletion already followed by this author's copy of its
     *     words, or of the `text` asked for                 -> landed: the paragraph comes back as it is, with no change
     *   - another author's insertion every word of which this
     *     author already struck                             -> landed: the same
     *
     * WHY a landed revert is recognised
     *   A card is built over the paragraph it already wrote whenever its preview
     *   refreshes after Apply, or when Apply runs again. Copying the words a second
     *   time doubled them in the document ("Customer Security Policy" four times).
     *
     * WHY the check against `expectedChange`
     *   The agent names a change from a reading of the paragraph it made earlier.
     *   When the paragraph changed since (a change accepted, a word typed), the same
     *   id can name other words; asking again is the only honest answer, and the
     *   re-ask lists what the paragraph holds now.
     */
    revertChange(request: RevertChangeRequest): RevertChangeOutcome {
        const doc = XmlHost.domParser().parseFromString(request.paragraphXml, "application/xml");
        const model = new ParagraphModel(doc.documentElement);
        /** A preview strikes this author's own insertion like anyone else's; the document write vacates it, as Word does. */
        const splicer = new ParagraphSplicer(model, this.revision, ownInsertionEditPolicyOf(request.ownChangeWriting));
        const changes = model.pendingChanges();
        const summaries = changes.map(RedlineEngine.summaryOf);
        /**
         * WHEN the paragraph's own mark is struck (`w:pPr/w:rPr/w:del`)
         *   CONSEQUENCE
         *     handed back: a copy written inside it would be merged into the next paragraph on
         *     Accept All together with the struck mark. Reverting a struck paragraph is the
         *     design's next step (a clone of the paragraph after it) and is not written yet.
         */
        if (ParagraphRevisionMarks.isMarkDeleted(doc.documentElement)) {
            throw new RevertChangeReask("paragraphStruck", summaries, "The paragraph itself is struck (its paragraph mark is deleted), and a change inside a struck paragraph cannot be reverted yet: reverting a struck paragraph or table row is not supported.");
        }
        const namedChanges = RedlineEngine.changesNamed(request, changes, summaries, request.markOfTheRun);
        /**
         * WHEN the run marked the change, and an earlier revert of the run took it out (this
         *      author's own change, which Word's writing removes or un-strikes)
         *   CONSEQUENCE
         *     the revert landed with that earlier one: the paragraph comes back as it is.
         */
        if (namedChanges.length === 0) return { paragraphXml: request.paragraphXml, appliedChangeCount: 0 };
        /**
         * WHEN typing inside the change cut it into pieces (`changeCutIntoPieces`)
         *   CONSEQUENCE
         *     every piece is reverted at its own place, and the words typed between them stay.
         */
        if (namedChanges.length > 1) return this.revertChangeCutIntoPieces(request, splicer, model, doc, namedChanges, summaries);
        const [change] = namedChanges;
        if (change.kind === "ins" && request.text !== null) throw new RevertChangeReask("textOnInsertion", summaries, `${request.changeId} is an insertion, which is struck or removed; text is only for a deletion.`);
        /**
         * The accepted span the restored words occupy once the model is re-read,
         * found through the runs that hold them, which outlive the refresh.
         *
         * NULL
         *   Nothing of the restored runs reads as live text, which the writes above
         *   never leave behind for a deletion; the text edit is then skipped.
         */
        const spanOfRuns = (runs: Element[]): AcceptedTextSpan | null => {
            const restoredPieces = model.acceptedPieces().filter((piece) => runs.includes(piece.run));
            if (restoredPieces.length === 0) return null;
            return { startOffset: restoredPieces[0].startOffset, endOffset: restoredPieces[restoredPieces.length - 1].endOffset };
        };
        let restoredSpan: AcceptedTextSpan | null = null;
        if (change.kind === "del") {
            /**
             * WHEN the deletion is this author's own and the revert is written as Word writes it
             *   CONSEQUENCE
             *     the words are un-struck in place, with no copy beside them to look for.
             */
            const ownDeletionRestoredInPlace = this.revision.isBy(change.author) && request.ownChangeWriting === "asWord";
            if (!ownDeletionRestoredInPlace && this.copyAlreadyBesideDeletion(splicer, change, request.text)) return { paragraphXml: request.paragraphXml, appliedChangeCount: 0 };
            const restoredRuns = ownDeletionRestoredInPlace
                ? RedlineEngine.restoreOwnDeletionWhole(splicer, change.pieces)
                : splicer.insertCopyOfStruckRuns(change.pieces);
            model.refresh();
            /**
             * WHEN the restored words run into a word beside them across a space this author struck
             *   CONSEQUENCE
             *     that space is un-struck and the model re-read, so the span below is the words' own.
             */
            if (this.restoreOwnStruckSpacesBeside(splicer, model, restoredRuns)) model.refresh();
            restoredSpan = spanOfRuns(restoredRuns);
        } else {
            const survivingPieces = change.pieces.filter((piece) => !piece.isDeleted);
            /**
             * WHEN every word of their insertion is struck by this author
             *   CONSEQUENCE
             *     the revert landed on an earlier build: nothing to write.
             */
            if (survivingPieces.length === 0 && change.pieces.every((piece) => this.revision.isBy(piece.deletedBy))) return { paragraphXml: request.paragraphXml, appliedChangeCount: 0 };
            if (survivingPieces.length === 0) throw new RevertChangeReask("nothingToRevert", summaries, `${request.changeId} has no surviving word to strike; every word of it is already struck.`);
            /**
             * EXAMPLE
             *   surviving pieces at 31..39 and 39..47 -> the insertion's span 31..47, struck in one deletion
             *   with one of the runs of spaces around it where both sides hold one (`strikeSpanOfRevertedInsertion`)
             */
            const insertionSpan: AcceptedTextSpan = { startOffset: survivingPieces[0].startOffset, endOffset: survivingPieces[survivingPieces.length - 1].endOffset };
            /**
             * CASES
             *   - the card writes words into the gap the insertion leaves -> the insertion alone: the words
             *                                                               written there stand between the
             *                                                               two spaces, so no double space is left
             *   - it writes none there                                    -> one of the two runs of spaces with it
             *                                                               (decision 5)
             */
            const strikeSpan = RedlineEngine.cardWritesWordsIntoTheGap(model.text, insertionSpan, request.textTheCardWritesAfter ?? null)
                ? insertionSpan
                : RedlineEngine.strikeSpanOfRevertedInsertion(model.text, insertionSpan);
            splicer.applyEdit({ ...strikeSpan, replacementText: "" }, null);
        }
        const revertedParagraphXml = XmlHost.xmlSerializer().serializeToString(doc);
        if (request.text === null || restoredSpan === null) return { paragraphXml: revertedParagraphXml, appliedChangeCount: 1 };
        const edited = this.redline({ paragraphXml: revertedParagraphXml, textToWrite: request.text, editableSpan: restoredSpan, hyperlinkTargets: request.hyperlinkTargets, ownChangeWriting: request.ownChangeWriting });
        return { paragraphXml: edited.paragraphXml, appliedChangeCount: 1 + edited.appliedChangeCount };
    }

    /**
     * Reverts a change that typing inside it cut into pieces (`changeCutIntoPieces`): each
     * piece at its own place, the last piece first, so the words typed between them stay
     * where they were typed and every earlier piece keeps the offsets it was read at.
     *
     * CASES, by the pieces' kind and author, as `revertChange` reverts one change
     *   - another author's insertion -> each piece's surviving words struck, with one of the runs of
     *                                   spaces around it where both sides hold one, read on the
     *                                   paragraph as the strikes after it left it
     *   - this author's insertion    -> each piece removed outright when written "asWord"
     *   - another author's deletion  -> a copy of each piece's struck words right after that piece
     *   - this author's deletion     -> each piece un-struck in place when written "asWord"
     *   - a piece already reverted   -> left as it is: a piece of an insertion every word of which is
     *                                   struck, a deletion piece with its copy already beside it
     *   - every piece already reverted -> landed: the paragraph comes back as it is, with no change
     *
     * WHY text on a cut deletion is handed back
     *   The text says how the deleted words should read once restored. The words typed
     *   between the pieces sit inside that stretch, so editing it toward the text would
     *   rewrite them as well; the model is asked again to revert the deletion with no
     *   text and edit the restored words after.
     *
     * EXAMPLE
     *   "Supplier's total liab" (Supplier Counsel) + "ASDF" (the user) + "ility under … $1.00 USD." (Supplier Counsel)
     *   -> the second piece struck, then the first -> the paragraph reads "ASDF"
     */
    private revertChangeCutIntoPieces(request: RevertChangeRequest, splicer: ParagraphSplicer, model: ParagraphModel, doc: Document, pieces: PendingChange[], summaries: PendingChangeSummary[]): RevertChangeOutcome {
        const [firstPiece] = pieces;
        const pieceIds = pieces.map((piece) => piece.id).join(", ");
        if (firstPiece.kind === "ins" && request.text !== null) throw new RevertChangeReask("textOnInsertion", summaries, `${request.changeId} is an insertion, which is struck or removed; text is only for a deletion.`);
        if (firstPiece.kind === "del" && request.text !== null) {
            throw new RevertChangeReask("staleChange", summaries, `${request.changeId} was cut into pieces (${pieceIds}) by words typed inside it, and text would rewrite those words too. Revert it with no text, then edit the restored words.`);
        }
        /** A deletion of this author's own, written as Word writes it, is un-struck in place, as `revertChange` does for one change. */
        const ownDeletionRestoredInPlace = firstPiece.kind === "del" && this.revision.isBy(firstPiece.author) && request.ownChangeWriting === "asWord";
        let revertedPieceCount = 0;
        for (const piece of [...pieces].reverse()) {
            if (piece.kind === "ins") {
                const survivingPieces = piece.pieces.filter((textPiece) => !textPiece.isDeleted);
                /** WHEN every word of this piece is already struck CONSEQUENCE it was reverted already: nothing to strike. */
                if (survivingPieces.length === 0) continue;
                /**
                 * EXAMPLE
                 *   surviving text pieces at 25..60 and 60..78 -> the piece's span 25..78
                 */
                const pieceSpan: AcceptedTextSpan = { startOffset: survivingPieces[0].startOffset, endOffset: survivingPieces[survivingPieces.length - 1].endOffset };
                splicer.applyEdit({ ...RedlineEngine.strikeSpanOfRevertedInsertion(model.text, pieceSpan), replacementText: "" }, null);
            } else if (ownDeletionRestoredInPlace) {
                RedlineEngine.restoreOwnDeletionWhole(splicer, piece.pieces);
            } else {
                /** WHEN this piece's copy already sits beside it CONSEQUENCE it was reverted already: nothing to copy. */
                if (this.copyAlreadyBesideDeletion(splicer, piece, null)) continue;
                splicer.insertCopyOfStruckRuns(piece.pieces);
            }
            model.refresh();
            revertedPieceCount += 1;
        }
        /** WHEN every piece was reverted already CONSEQUENCE the revert landed on an earlier build: nothing written. */
        if (revertedPieceCount === 0) return { paragraphXml: request.paragraphXml, appliedChangeCount: 0 };
        return { paragraphXml: XmlHost.xmlSerializer().serializeToString(doc), appliedChangeCount: 1 };
    }

    /**
     * The span a revert of an insertion strikes: the insertion's surviving words
     * and, where whitespace stands on both sides of them (a space, a tab or a line
     * break alike: `WordTokenizer.isWhitespace`), one of the two runs with them.
     *
     * WHY
     *   Counsel's "sixty (60)" replaced "thirty (30)" between "within " and " days".
     *   Reverting the insertion alone left "within  days", a run of spaces the model
     *   never saw, which the card then proposed (live test 2.10, 2026-10-15); the
     *   applier handed back the list over it and asked the model to replace counsel's
     *   words instead. The re-ask went for this rule: the revert keeps one run, in the
     *   engine both the web's
     *   `RevertOperations` and the add-in's `ProposalBuilder.applyReverts` call, so
     *   the card and the Word write read alike.
     *
     * CASES, by the runs of whitespace either side of the insertion
     *   - whitespace on one side only, or on neither  -> the insertion alone: "Pay " + [promptly ] + "on" strikes "promptly "
     *   - whitespace on both sides, the runs as long  -> the insertion and the run after it
     *   - whitespace on both sides, one run shorter   -> the insertion and the shorter run; the longer is the
     *                                                    paragraph's own gap, the two spaces after a sentence
     *
     * EXAMPLE
     *   "within " + [sixty (60)] + " days"                  1 | 1 -> "sixty (60) " struck            -> "within days"
     *   "within\t" + [sixty (60)] + " days"                 1 | 1 -> "sixty (60) " struck            -> "within\tdays"
     *   "Fees are due.  " + [Interest accrues.] + "  Late"  2 | 2 -> the insertion and the "  " after -> "Fees are due.  Late"
     *   "Fees are due. " + [Interest accrues.] + "  Late"   1 | 2 -> the insertion and the " " before -> "Fees are due.  Late"
     */
    private static strikeSpanOfRevertedInsertion(acceptedText: string, insertionSpan: AcceptedTextSpan): AcceptedTextSpan {
        const spacesBeforeInsertion = WordTokenizer.whitespaceRunLengthEndingAt(acceptedText, insertionSpan.startOffset);
        const spacesAfterInsertion = WordTokenizer.whitespaceRunLengthStartingAt(acceptedText, insertionSpan.endOffset);
        /** WHEN no space stands on one of the insertion's sides CONSEQUENCE striking it alone doubles nothing. */
        if (spacesBeforeInsertion === 0 || spacesAfterInsertion === 0) return insertionSpan;
        /**
         * CASES
         *   - the run after is as long as the run before, or shorter -> struck with the insertion
         *   - the run before is the shorter                           -> struck instead
         *
         * EXAMPLE
         *   span 49..59, 1 space before and 1 after -> `59 + 1` = 60: 49..60, "sixty (60) "
         *   span 15..32, 1 before and 2 after       -> `15 - 1` = 14: 14..32, " Interest accrues."
         */
        if (spacesAfterInsertion <= spacesBeforeInsertion) return { startOffset: insertionSpan.startOffset, endOffset: insertionSpan.endOffset + spacesAfterInsertion };
        return { startOffset: insertionSpan.startOffset - spacesBeforeInsertion, endOffset: insertionSpan.endOffset };
    }

    /**
     * Whether the card writes words into the gap a reverted insertion leaves: the engine's
     * own plan (`TextEditPlanner.editsBetween`) from the paragraph with the insertion struck
     * alone to the text the card writes once its reverts are done, holding a words edit at
     * or inside the two runs of spaces that meet there.
     *
     * WHY decision 5 asks this first
     *   Decision 5 keeps one run of spaces where a reverted insertion leaves two. When the
     *   same card writes its own words into that gap, the two spaces never stand together:
     *   taken anyway, the space came back inside the card's insertion, `[- ][+forty-five
     *   (45) ]`, where main writes the words between plain spaces (§17, -26 and -38 at
     *   b656f43d8, bisected to 6f9de45ac, 2026-10-19).
     *
     * CASES
     *   - the card's text is not known (the web's revert planning) -> false: decision 5 holds
     *   - a words edit at or inside the gap                       -> true
     *   - only spacing, or words elsewhere                        -> false: decision 5 holds
     *
     * EXAMPLE
     *   "…within sixty (60) days…", the card writes "…within forty-five (45) days…"
     *   struck alone: "…within  days…"; the plan inserts "forty-five (45)" between the spaces -> true
     *   the card writes "…within days…" -> only a spacing edit -> false
     */
    private static cardWritesWordsIntoTheGap(acceptedText: string, insertionSpan: AcceptedTextSpan, textTheCardWritesAfter: string | null): boolean {
        /** WHEN the caller hands no card text (the web's revert planning) CONSEQUENCE false: decision 5 holds. */
        if (textTheCardWritesAfter === null) return false;
        /**
         * EXAMPLE
         *   "within sixty (60) days", insertion 7..17 -> slice(0, 7) "within " + slice(17) " days" = "within  days"
         */
        const textWithTheInsertionStruck = acceptedText.slice(0, insertionSpan.startOffset) + acceptedText.slice(insertionSpan.endOffset);
        /**
         * EXAMPLE, the gap in "within  days", where the insertion stood at 7
         *   one space ends at 7, one starts at 7 -> `7 - 1` = 6 to `7 + 1` = 8
         */
        const gapStartOffset = insertionSpan.startOffset - WordTokenizer.whitespaceRunLengthEndingAt(textWithTheInsertionStruck, insertionSpan.startOffset);
        const gapEndOffset = insertionSpan.startOffset + WordTokenizer.whitespaceRunLengthStartingAt(textWithTheInsertionStruck, insertionSpan.startOffset);
        /**
         * A words edit whose span meets the gap `[gapStartOffset, gapEndOffset]`, both ends
         * included, so an insertion right at either space counts.
         *
         * EXAMPLE, "within  days", gap 6..8
         *   card "within forty-five (45) days": words edit 7..7 "forty-five (45) "  -> 7 <= 8 and 7 >= 6: true
         *   card "within days":                 spacing edit 6..7 ""                 -> not words: false
         *   card "within  days, net":           words edit 12..12 ", net"           -> 12 > 8: false
         */
        return TextEditPlanner.editsBetween(textWithTheInsertionStruck, textTheCardWritesAfter)
            .some((plannedEdit) => plannedEdit.kind === "words" && plannedEdit.startOffset <= gapEndOffset && plannedEdit.endOffset >= gapStartOffset);
    }

    /**
     * Un-strikes the spaces this author struck beside restored words where, without
     * them, those words would run into the word on that side. True when any came back.
     *
     * WHY
     *   A revert of counsel's insertion strikes one of the two runs of spaces around it
     *   (`strikeSpanOfRevertedInsertion`), so "within  days" reads "within days". A
     *   revert of the deletion beside that insertion, in the same list or a later turn,
     *   puts its words back right after the deletion, against the struck space:
     *   "within thirty (30)days". The space one revert took, the other gives back.
     *
     * CASES, on each side of the restored words
     *   - a space, or the paragraph's edge, is next to them in the accepted text  -> nothing to bring back
     *   - a word is next to them, and the struck text between holds a piece of
     *     spaces alone that this author struck                                     -> that piece is un-struck
     *   - a word is next to them and no such piece lies between                    -> left as it reads: the
     *                                                                                 glue is the document's own
     *
     * EXAMPLE
     *   "within " [del thirty (30)] [ins thirty (30)] [struck sixty (60)] [struck " "] "days"
     *   accepted "within thirty (30)days": ")" meets "d" across the struck " " -> it comes back -> "within thirty (30) days"
     */
    private restoreOwnStruckSpacesBeside(splicer: ParagraphSplicer, model: ParagraphModel, restoredRuns: Element[]): boolean {
        const pieces = model.pieces;
        const restoredPieceIndexes = pieces.flatMap((piece, pieceIndex) => (restoredRuns.includes(piece.run) ? [pieceIndex] : []));
        /** WHEN no piece of the restored runs is in the model CONSEQUENCE there are no restored words to read beside. */
        if (restoredPieceIndexes.length === 0) return false;
        const firstRestoredIndex = restoredPieceIndexes[0];
        const lastRestoredIndex = restoredPieceIndexes[restoredPieceIndexes.length - 1];
        const struckSpacesToRestore: RestorableRange[] = [
            ...RedlineEngine.ownStruckSpacesBetween(pieces, lastRestoredIndex, +1, this.revision),
            ...RedlineEngine.ownStruckSpacesBetween(pieces, firstRestoredIndex, -1, this.revision),
        ];
        if (struckSpacesToRestore.length === 0) return false;
        splicer.restoreDeletions(struckSpacesToRestore);
        return true;
    }

    /**
     * The pieces of spaces alone this author struck between the restored piece at
     * `restoredPieceIndex` and the next live piece in `direction`, when the restored
     * words and that live piece would otherwise meet with no whitespace between them.
     *
     * CASES
     *   - no live piece lies in that direction (the paragraph's edge)         -> []
     *   - the restored words or the live piece end in whitespace on that side -> []: nothing runs into anything
     *   - struck pieces lie between, none of them this author's spaces alone  -> []
     *   - otherwise                                                           -> each such piece, whole
     *
     * EXAMPLE, direction +1 from the restored "thirty (30)" at index 2
     *   index 3 struck "sixty (60)" (this author's, not spaces) -> passed over
     *   index 4 struck " " (this author's, spaces alone)        -> collected
     *   index 5 live "days": ")" and "d" are both not whitespace -> [the piece at 4, 0..1]
     */
    private static ownStruckSpacesBetween(pieces: readonly TextPiece[], restoredPieceIndex: number, direction: 1 | -1, revision: Revision): RestorableRange[] {
        const struckSpaces: RestorableRange[] = [];
        /**
         * EXAMPLE
         *   restoredPieceIndex 2, direction +1 -> 3, 4, 5 … until a live piece; direction -1 -> 1, 0
         */
        for (let pieceIndex = restoredPieceIndex + direction; pieceIndex >= 0 && pieceIndex < pieces.length; pieceIndex += direction) {
            const piece = pieces[pieceIndex];
            if (piece.isDeleted) {
                /** EXAMPLE "sixty (60)" struck by this author is passed over; " " struck by this author is collected whole, 0..1. */
                if (revision.isBy(piece.deletedBy) && WordTokenizer.isWhitespaceOnly(piece.text)) struckSpaces.push({ piece, fromOffsetInPiece: 0, toOffsetInPiece: piece.text.length });
                continue;
            }
            /**
             * The two characters that would meet: the restored words' last and the live piece's first
             * going forward, the live piece's last and the restored words' first going back.
             *
             * EXAMPLE
             *   forward: restored "thirty (30)" -> ")", live "days" -> "d"
             *   back:    live "within " -> " ": a space already stands there -> []
             */
            const restoredEdgeCharacter = direction === 1 ? RedlineEngine.lastCharacterOf(pieces[restoredPieceIndex].text) : RedlineEngine.firstCharacterOf(pieces[restoredPieceIndex].text);
            const liveEdgeCharacter = direction === 1 ? RedlineEngine.firstCharacterOf(piece.text) : RedlineEngine.lastCharacterOf(piece.text);
            const wordsWouldMeet = !WordTokenizer.isWhitespace(restoredEdgeCharacter) && !WordTokenizer.isWhitespace(liveEdgeCharacter);
            return wordsWouldMeet ? struckSpaces : [];
        }
        return [];
    }

    /**
     * The first character of a piece's text, "" for an empty piece.
     *
     * EXAMPLE
     *   "days" -> "d"
     */
    private static firstCharacterOf(text: string): string {
        return text.length === 0 ? "" : text[0];
    }

    /**
     * The last character of a piece's text, "" for an empty piece.
     *
     * EXAMPLE
     *   "thirty (30)" -> text[`11 - 1`] = ")"
     */
    private static lastCharacterOf(text: string): string {
        return text.length === 0 ? "" : text[text.length - 1];
    }

    /**
     * True when every `w:del` holding the change's pieces is already followed by
     * this author's `w:ins` reading as its struck words, or as the `text` the
     * revert asks for: the copy `insertCopyOfStruckRuns` writes, found where it
     * writes it.
     *
     * CASES, per deletion
     *   - the next element is this author's insertion reading as the struck words  -> landed as they were
     *   - the next element is this author's insertion reading as `textAskedFor`    -> landed and already edited toward the text
     *   - anything else (their insertion, plain text, no element)                  -> not landed
     *
     * EXAMPLE
     *   <w:del author="Counsel">the State of New York</w:del><w:ins author="james">the State of New York</w:ins>
     *   -> true: the copy sits right after the deletion with the same words
     */
    private copyAlreadyBesideDeletion(splicer: ParagraphSplicer, change: PendingChange, textAskedFor: string | null): boolean {
        const deletions: Element[] = [];
        for (const piece of change.pieces) {
            const deletion = splicer.deletionHolding(piece.run);
            if (!deletions.includes(deletion)) deletions.push(deletion);
        }
        return deletions.every((deletion) => {
            const copies = this.ownInsertionsRightAfter(deletion);
            if (copies.length === 0) return false;
            const copyText = copies.map((copy) => RedlineEngine.textOfDescendants(copy, "t")).join("");
            const struckText = RedlineEngine.textOfDescendants(deletion, "delText");
            /** Read under the fold: a copy edited toward a `text` the model retyped with a straight apostrophe reads as that text. */
            return WordTokenizer.readsTheSame(copyText, struckText) || (textAskedFor !== null && WordTokenizer.readsTheSame(copyText, textAskedFor));
        });
    }

    /**
     * This author's insertions that follow `element` without a break: the copy a
     * revert wrote, and the pieces an edit toward the revert's `text` left it in.
     * Markers that carry no content (bookmark and comment range starts and ends,
     * proofing marks) do not break the run; any other element ends it.
     *
     * EXAMPLE
     *   <w:del author="Counsel">within one (1) business day </w:del>
     *   <w:ins author="james">within </w:ins><w:ins author="james">two (2)</w:ins><w:ins author="james"> business days </w:ins>
     *   -> the three insertions, reading together "within two (2) business days "
     */
    private ownInsertionsRightAfter(element: Element): Element[] {
        const insertions: Element[] = [];
        for (let sibling = element.nextSibling; sibling !== null; sibling = sibling.nextSibling) {
            if (sibling.nodeType !== 1) continue;
            const candidate = sibling as Element;
            if (CONTENTLESS_MARKERS.has(candidate.localName)) continue;
            if (candidate.localName !== "ins" || !this.revision.isBy(candidate.getAttributeNS(WORDPROCESSINGML_NS, "author"))) break;
            insertions.push(candidate);
        }
        return insertions;
    }

    /** The text of every `w:<localName>` descendant of `element`, in document order. */
    private static textOfDescendants(element: Element, localName: string): string {
        return Array.from(element.getElementsByTagNameNS(WORDPROCESSINGML_NS, localName)).map((textElement) => textElement.textContent ?? "").join("");
    }

    /**
     * Un-strikes every piece of this author's own deletion and returns the runs
     * that hold the words, which `restoreDeletions` keeps as they are.
     */
    private static restoreOwnDeletionWhole(splicer: ParagraphSplicer, pieces: TextPiece[]): Element[] {
        const runs: Element[] = [];
        for (const piece of pieces) {
            if (!runs.includes(piece.run)) runs.push(piece.run);
        }
        /**
         * EXAMPLE
         *   piece.text = "one (1)" -> the range 0..7, the whole piece
         */
        splicer.restoreDeletions(pieces.map((piece) => ({ piece, fromOffsetInPiece: 0, toOffsetInPiece: piece.text.length })));
        return runs;
    }

    /**
     * One pending change as a re-ask lists it and as a revert is matched against it.
     *
     * CASES, for the words
     *   - a deletion   -> every struck word
     *   - an insertion -> its surviving words, the ones Accept All keeps
     */
    private static summaryOf(change: PendingChange): PendingChangeSummary {
        return {
            id: change.id,
            kind: change.kind,
            author: change.author,
            text: change.pieces.filter((piece) => change.kind === "del" || !piece.isDeleted).map((piece) => piece.text).join(""),
        };
    }

    /** The paragraph's pending changes in one line, for a re-ask: `c1 (Counterparty's deletion: "within one (1) business day ")`. */
    static listed(summaries: PendingChangeSummary[]): string {
        if (summaries.length === 0) return "no pending change";
        return summaries.map((summary) => `${summary.id} (${summary.author}'s ${summary.kind === "del" ? "deletion" : "insertion"}: ${JSON.stringify(summary.text)})`).join(", ");
    }

    /**
     * The change a revert names. The one resolver of a revert's change: `revertChange`,
     * `changesARevertNamesOn` and every caller of them go through it.
     *
     * Within a run that laid marks, by the mark alone (`changesMarkedFor`). Otherwise by the
     * change as it was read (kind, author, words) first and by its id second.
     *
     * RETURNS
     *   One change, or the pieces typing inside it cut it into, in document order; none when
     *   the run's mark names a change an earlier revert of the run took out.
     *
     * WHY the words first
     *   The id numbers the paragraph's changes as they stood when the agent read
     *   it. A card applied since, or the revert before this one in the same card,
     *   moves the numbering (their deletion c1 restored puts our insertion at c2
     *   and their insertion at c3), while the change itself still reads as the
     *   agent read it. So the id is how the agent names the change, and the
     *   reading is what the revert resolves.
     *
     * CASES
     *   - one change reads as expected            -> that change, whatever its id is now
     *   - several do, and one carries the id      -> that one
     *   - several do, and none carries the id     -> handed back as ambiguous, naming them
     *   - none does, but typing inside it cut it
     *     into pieces that read as it together    -> the pieces (`changeCutIntoPieces`)
     *   - none does, but a change carries the id  -> handed back as stale: the id's change reads differently
     *   - none does, and none carries the id      -> handed back as unknown
     */
    private static changesNamed(request: RevertTargetName, changes: PendingChange[], summaries: PendingChangeSummary[], markOfTheRun: RevertMarkOfTheRun | undefined): PendingChange[] {
        /** NULL: no run marked this revert's change; it is resolved by the change as read, and its id. */
        if (markOfTheRun !== undefined) return RedlineEngine.changesMarkedFor(request, changes, summaries, markOfTheRun);
        const expected = request.expectedChange;
        /**
         * CASES, for the words compared (`WordTokenizer.readsTheSame`)
         *   - the summary's words: the struck words of a deletion, the surviving words of an insertion
         *   - every word of the change, struck or not: an insertion this author has since struck
         *     whole reads as "" by its surviving words and as the agent read it by all of them
         */
        const readsAsExpected = (change: PendingChange, summary: PendingChangeSummary): boolean =>
            summary.kind === expected.kind
            && Revision.sameAuthor(summary.author, expected.author)
            && (WordTokenizer.readsTheSame(summary.text, expected.text) || WordTokenizer.readsTheSame(change.pieces.map((piece) => piece.text).join(""), expected.text));
        const matching = changes.filter((change, index) => readsAsExpected(change, summaries[index]));
        if (matching.length === 1) return matching;
        const listed = RedlineEngine.listed(summaries);
        if (matching.length > 1) {
            const carryingTheId = matching.find((change) => change.id === request.changeId);
            if (carryingTheId !== undefined) return [carryingTheId];
            throw new RevertChangeReask("ambiguousChange", summaries, `${matching.length} changes read as ${request.changeId} was read (${matching.map((change) => change.id).join(", ")}) and none carries that id now; the paragraph holds ${listed}.`);
        }
        const cutIntoPieces = RedlineEngine.changeCutIntoPieces(request, changes, summaries);
        /** WHEN the change was cut into pieces by typing inside it CONSEQUENCE its pieces are reverted, each at its place. */
        if (cutIntoPieces !== null) return cutIntoPieces;
        const carryingTheId = changes.find((change) => change.id === request.changeId);
        if (carryingTheId !== undefined) throw new RevertChangeReask("staleChange", summaries, `${request.changeId} no longer names the change that was read; the paragraph holds ${listed}.`);
        throw new RevertChangeReask("unknownChange", summaries, `The paragraph has no pending change ${request.changeId}; it holds ${listed}.`);
    }

    /**
     * The change a revert names within a run that laid marks: the changes whose revision
     * elements carry the revert's name, never a reading of the paragraph as it stands now.
     *
     * WHY never the reading
     *   Once other cards of the run wrote in the paragraph, the id names another change, and
     *   two changes can read alike (three "shall not knowingly " insertions, one of them cut in
     *   two by our "ever "). Reading again there wrote the revert onto the wrong change. The
     *   mark sits on the change's own elements, which no write of ours moves, and both halves
     *   of an element our edit cut carry it.
     *
     * CASES
     *   - the first read did not find the change           -> handed back with that read's reason;
     *                                                         it is never resolved again
     *   - one change carries the name                       -> that change, its marked pieces alone
     *   - several do                                        -> the pieces of the change our edit cut,
     *                                                         in document order, each its marked pieces alone
     *   - none does, the name having been laid              -> none: an earlier revert of the run took
     *                                                         the change out (this author's own, removed
     *                                                         or un-struck), so this one landed with it
     *   - the name was neither laid nor left unresolved     -> handed back as unknown: the run never read
     *                                                         this revert, so no change is known for it
     *
     * EXAMPLE
     *   first read   (i) c1, (j) c2, (k) c3, each DXC Legal "shall not knowingly "; (j) marked
     *   now          c1 DXC "shall not ", c2 ours "ever ", c3 DXC "knowingly ", c4 (j), c5 (k)
     *   ->           [c4], the change whose element carries the mark
     *
     * WHY the marked pieces alone
     *   The reader joins insertions by one author that touch into one change. An earlier revert
     *   of the run can have written an insertion of its own beside the marked one, and that
     *   insertion carries no mark. Taking the whole change struck the words the earlier revert
     *   had just restored together with the marked words.
     *
     * EXAMPLE
     *   the user's deletion "thirty (30)" (c1) and insertion "forty-five (45)" (c2), c1 reverted first
     *   now          c2 the user's "thirty (30)forty-five (45)": the restored words, then the marked insertion
     *   ->           [c2 holding the piece "forty-five (45)" alone], so "thirty (30)" stays
     */
    private static changesMarkedFor(request: RevertTargetName, changes: PendingChange[], summaries: PendingChangeSummary[], markOfTheRun: RevertMarkOfTheRun): PendingChange[] {
        const { revertName, targetsOfTheRun } = markOfTheRun;
        const unresolvedReask = targetsOfTheRun.unresolvedRevertReasks.get(revertName);
        /** WHEN the first read did not find the change CONSEQUENCE handed back with that read's reason, never read again. */
        if (unresolvedReask !== undefined) throw unresolvedReask;
        /** WHEN the run never read this revert CONSEQUENCE no change is known for it: handed back as unknown. */
        if (!targetsOfTheRun.markedRevertNames.has(revertName)) {
            throw new RevertChangeReask("unknownChange", summaries, `${request.changeId} was not read before the run wrote; the paragraph holds ${RedlineEngine.listed(summaries)}.`);
        }
        const expected = request.expectedChange;
        return changes.flatMap((change): PendingChange[] => {
            /** WHEN the change is of another kind or by another author than the revert names CONSEQUENCE it is not the revert's change. */
            if (change.kind !== expected.kind || !Revision.sameAuthor(change.author, expected.author)) return [];
            const piecesCarryingTheName = change.pieces.filter((piece) => RedlineEngine.pieceCarriesRevertName(change.kind, piece, revertName));
            /** WHEN no piece of the change sits in a revision element carrying the name CONSEQUENCE it is not the revert's change. */
            if (piecesCarryingTheName.length === 0) return [];
            return [{ ...change, pieces: piecesCarryingTheName }];
        });
    }

    /**
     * Whether the revision element a piece sits in carries a revert's name: the insertion
     * element of a piece of an insertion, the deletion element of a piece of a deletion.
     *
     * EXAMPLE
     *   a piece of DXC Legal's insertion whose `w:ins` carries the name, every word of it struck since -> true:
     *   the strike sits inside the marked element
     */
    private static pieceCarriesRevertName(changeKind: PendingChangeKind, piece: TextPiece, revertName: string): boolean {
        /**
         * CASES
         *   - an insertion -> the insertion element the piece was read inside
         *   - a deletion   -> the deletion element
         */
        const revisionElement = changeKind === "ins" ? piece.insertionContainer : piece.deletionContainer;
        return revisionElement !== null && revertTargetNamesOn(revisionElement).includes(revertName);
    }

    /**
     * The change the caller read, found again after typing inside it cut it into pieces:
     * a run of the paragraph's changes of its kind and by its author, in document order,
     * whose words read together as the change was read. The changes of other authors
     * between them are what cut it. The pieces come back in document order, so the revert
     * takes back every piece and leaves the words typed between them
     * (`revertChangeCutIntoPieces`).
     *
     * WHY
     *   The user typed words inside Supplier Counsel's insertion "Supplier's total
     *   liability under this Agreement will not exceed $1.00 USD." after the card that
     *   takes it back was proposed. Word split the one insertion into pieces (c1, c3, c4
     *   by Supplier Counsel, with c2 by the user between them), so no single change read
     *   as the card's and the revert was handed back as stale, though every word of the
     *   insertion was still there under its author.
     *
     * CASES
     *   - exactly one run of two or more such changes reads as the change was read -> those changes
     *   - none does                                                                -> null: stale or unknown, as before
     *   - several do                                                               -> null: which run is meant is not known
     *
     * EXAMPLE
     *   read      Supplier Counsel's insertion "Supplier's total liability under this Agreement will not exceed $1.00 USD."
     *   changes   c1 Supplier Counsel "Supplier's total liab"
     *             c2 the user         "ASDF"
     *             c3 Supplier Counsel "ility under this Agreement will not exceed $1.00 USD."
     *   run       c1 + c3 -> "Supplier's total liab" + "ility under this Agreement will not exceed $1.00 USD.", which reads as read
     */
    private static changeCutIntoPieces(request: RevertTargetName, changes: PendingChange[], summaries: PendingChangeSummary[]): PendingChange[] | null {
        const expected = request.expectedChange;
        /** The changes of the kind and author read, by their index in `changes` and so in document order. */
        const indexesOfTheLayer = changes.flatMap((change, index) => (change.kind === expected.kind && Revision.sameAuthor(change.author, expected.author) ? [index] : []));
        /**
         * Whether a run of the layer's changes reads as the change was read, by the same two
         * readings `changesNamed` compares one change by: their summaries' words, and every word
         * of them struck or not.
         */
        const runReadsAsExpected = (runIndexes: number[]): boolean =>
            WordTokenizer.readsTheSame(runIndexes.map((index) => summaries[index].text).join(""), expected.text)
            || WordTokenizer.readsTheSame(runIndexes.flatMap((index) => changes[index].pieces).map((piece) => piece.text).join(""), expected.text);
        const runsReadingAsExpected: number[][] = [];
        for (let firstPosition = 0; firstPosition < indexesOfTheLayer.length; firstPosition++) {
            /**
             * EXAMPLE
             *   layer indexes [0, 2, 3], firstPosition 0
             *   `+ 1` -> lastPosition starts at 1: a run holds at least two changes, since one change alone
             *            was already compared by `changesNamed`
             *   `+ 1` in the slice -> [0, 2] for lastPosition 1, [0, 2, 3] for lastPosition 2
             */
            for (let lastPosition = firstPosition + 1; lastPosition < indexesOfTheLayer.length; lastPosition++) {
                const runIndexes = indexesOfTheLayer.slice(firstPosition, lastPosition + 1);
                if (runReadsAsExpected(runIndexes)) runsReadingAsExpected.push(runIndexes);
            }
        }
        /** WHEN no run, or more than one, reads as the change was read CONSEQUENCE no piece of it is chosen. */
        if (runsReadingAsExpected.length !== 1) return null;
        const [runIndexes] = runsReadingAsExpected;
        return runIndexes.map((index) => changes[index]);
    }
}
